import React from 'react';
import {
  apiGetDepots,
  apiGetInputProducts,
  apiGetInputProductSubSubCategories,
  apiGetProductDepotInfo,
} from 'constants/Api';
import {connect} from 'react-redux';
import {
  IDepot,
  IFarmInputProduct,
  IFarmInputProductDepotInfo,
  ISupplierUserProfile,
} from 'constants/interfaces';
import {Box, Button, Snackbar, Typography} from '@material-ui/core';
import {KhulaSupplierTable, Row} from 'components/Table/KhulaSupplierTable';
import {
  getDefaultFilters,
  inventoryTableFilters,
  inventoryTableHeaders,
  inventoryTableRows,
} from 'screens/Dashboard/Inventory/InventoryTableSetup';
import {ESidePanelView} from 'constants/index';
import {InventorySidePanelData} from 'screens/Dashboard/utils/SidePanel/SidePanel';
import {
  reduxToggleSidePanel,
  reduxSetDepots,
  reduxSetProductDepotInfos,
  reduxSetProducts,
  reduxSetSubSubCategories,
} from 'appredux/actions';
import {ProductState} from 'appredux/reducers/reducer-products';
import moment from 'moment';
import {DepotState} from 'appredux/reducers/reducer-depots';
import {Skeleton} from '@material-ui/lab';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import InventoryCsvUploadDialog from 'screens/Dashboard/Inventory/InventoryCsvUploadDialog';
import {PermissionsService} from 'services/PermissionsService';

type ProductsProps = {
  user: ISupplierUserProfile;
  productState: ProductState;
  productDepotInfo: IFarmInputProductDepotInfo[];
  depotState: DepotState;
};

/**
 * Component used to display products
 */
class Inventory extends React.Component<ProductsProps> {
  state: {
    isLoading: boolean;
    isRefreshing: boolean;
    showRefreshedSnackbar: boolean;
    selectedDepotIds: string[];
    showUploadDialog: boolean;
    showUploadSuccessSnackbar: boolean;
  } = {
    isLoading: true,
    isRefreshing: false,
    showRefreshedSnackbar: false,
    selectedDepotIds: [],
    showUploadDialog: false,
    showUploadSuccessSnackbar: false,
  };

  componentDidMount() {
    const {productState} = this.props;
    const noProducts = !productState.products?.length;
    this.setState({isLoading: noProducts}, () => {
      this.loadProducts()
        .then(() => this.loadDepots())
        .then(() => this.fetchDepotInfo(this.state.selectedDepotIds))
        .catch((err) => {
          alert('Error loading products. Please try again later');
          console.error(err);
        })
        .finally(() => this.setState({isLoading: false}));
    });
  }

  loadProducts(): Promise<void> {
    const {user, productState} = this.props;
    const {lastUpdate, products} = productState;

    const productsAreExpired = moment(lastUpdate).isBefore(
      moment().subtract(8, 'hours'),
    );
    const noProducts = !products?.length;

    if (productsAreExpired || noProducts)
      return apiGetInputProducts(user)
        .then((products) => reduxSetProducts(products))
        .then(() => {});

    return Promise.resolve();
  }

  loadDepots(): Promise<void> {
    const {user, depotState} = this.props;
    const {lastUpdate, depots} = depotState;

    const depotsAreExpired = moment(lastUpdate).isBefore(
      moment().subtract(24, 'hours'),
    );
    const noDepots = !depots?.length;

    if (depotsAreExpired || noDepots)
      return apiGetDepots(user)
        .then((depots) => reduxSetDepots(depots))
        .then(() => {});

    return Promise.resolve();
  }

  refreshInputProducts() {
    const {user} = this.props;

    this.setState({isRefreshing: true}, () => {
      apiGetInputProducts(this.props.user)
        .then((products: IFarmInputProduct[]) => {
          reduxSetProducts(products);
          this.setState({
            isRefreshing: false,
            showRefreshedSnackbar: true,
          });
        })
        .then(() => apiGetInputProductSubSubCategories())
        .then((subSubCategories) => reduxSetSubSubCategories(subSubCategories))
        .then(() => apiGetDepots(user))
        .then((depots) => reduxSetDepots(depots))
        .then(() => this.fetchDepotInfo(this.state.selectedDepotIds))
        .catch((err) => {
          alert('Error refreshing products. Please try again later');
          console.error(err);
        })
        .finally(() => this.setState({isRefreshing: false}));
    });
  }

  editProduct(product: IFarmInputProduct) {
    const productSidePanelData: InventorySidePanelData = {
      product,
      depotIds: this.state.selectedDepotIds,
    };
    const type = {
      panelType: ESidePanelView.EDIT_INVENTORY,
      data: productSidePanelData,
    };
    reduxToggleSidePanel(true, type, false);
  }

  handleSelectedDepotsChange(selectedDepots: IDepot[]) {
    const selectedDepotIds = selectedDepots.map((depot) => depot.id.toString());
    this.setState({selectedDepotIds});
    this.fetchDepotInfo(selectedDepotIds);
  }

  fetchDepotInfo(depotIds: string[]) {
    if (!depotIds?.length) {
      reduxSetProductDepotInfos([]);
      return;
    }
    this.setState({isRefreshing: true});
    apiGetProductDepotInfo({depotIds})
      .then((productDepotInfo) => reduxSetProductDepotInfos(productDepotInfo))
      .catch((err) => {
        alert('Error fetching depot information. Please try again later');
        console.error(err);
        reduxSetProductDepotInfos([]);
      })
      .finally(() => this.setState({isRefreshing: false}));
  }

  mapInventory(
    products: IFarmInputProduct[],
    productDepotInfo: IFarmInputProductDepotInfo[],
  ) {
    if (!this.state.selectedDepotIds.length) return [];

    const productDepotInfoMap: {
      [productId: string]: IFarmInputProductDepotInfo;
    } = {};
    productDepotInfo.forEach(
      (depotInfo) => (productDepotInfoMap[depotInfo.productId] = depotInfo),
    );

    const productMap: {
      [productId: string]: IFarmInputProduct;
    } = {};
    products.forEach(
      (product) => (productMap[product.id.toString()] = product),
    );

    const productsWithDepotInfo: IFarmInputProduct[] = productDepotInfo
      .filter((depotInfo) => productMap[depotInfo.productId])
      .map((depotInfo) => ({
        ...productMap[depotInfo.productId],
        depotInfo,
      }));

    const productsWithoutDepotInfo = products.filter(
      (product) => !productDepotInfoMap[product.id],
    );

    return [...productsWithDepotInfo, ...productsWithoutDepotInfo];
  }

  render() {
    const {user, productState, productDepotInfo, depotState} = this.props;
    const {products, lastUpdate} = productState;
    const {
      isLoading,
      isRefreshing,
      showRefreshedSnackbar,
      selectedDepotIds,
      showUploadDialog,
      showUploadSuccessSnackbar,
    } = this.state;

    const mapRowToProduct = (row: Row) =>
      // @ts-ignore
      products.find((product) => product.id == row.id);

    const depots = new PermissionsService(user).getEditableDepots(
      depotState.depots,
    );

    return (
      <div className="table-screen inventory-container">
        <Typography variant="h4" className="page-title">
          Inventory
        </Typography>
        <Typography variant="body1" className="page-subtitle">
          Your depot inventory
        </Typography>
        <Box className="actions-row">
          {isLoading && (
            <Skeleton
              width={240}
              height={40}
              variant={'rect'}
              style={{borderRadius: 8}}
            />
          )}
          {!isLoading && (
            <>
              <Autocomplete
                className="depot-select"
                multiple
                options={depots}
                getOptionLabel={(depot: IDepot) => depot.name}
                filterSelectedOptions
                size="small"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    variant="outlined"
                    label="Depots"
                  />
                )}
                onChange={(event, selectedDepots: IDepot[]) => {
                  this.handleSelectedDepotsChange(selectedDepots);
                }}
              />
              <Box flex={1} />
              {!user.isAECI && (
                <Button
                  variant="outlined"
                  // startIcon={<PublishIcon />}
                  onClick={() => this.setState({showUploadDialog: true})}
                  size="small"
                  color={'primary'}
                >
                  Upload
                </Button>
              )}
            </>
          )}
        </Box>
        <div className="products-section">
          <KhulaSupplierTable
            headers={inventoryTableHeaders(user?.company?.depotPriceList)}
            rows={inventoryTableRows(
              this.mapInventory(products || [], productDepotInfo),
              (product) => this.editProduct(product),
            )}
            defaultActiveFilters={getDefaultFilters()}
            defaultSortField={'name'}
            filtersGroups={inventoryTableFilters({
              showProductsView: selectedDepotIds?.length > 0,
            })}
            isLoading={isLoading}
            isRefreshing={isRefreshing}
            onRefresh={() => this.refreshInputProducts()}
            onRowSelect={(row: any) => {
              const product = mapRowToProduct(row);
              if (!product) return;
              this.editProduct(product);
            }}
          />
          <Box mt={1}>
            <Typography variant={'caption'}>
              Last updated: {moment(lastUpdate).fromNow()}
            </Typography>
          </Box>
          <Snackbar
            anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
            open={showRefreshedSnackbar}
            onClose={() => this.setState({showRefreshedSnackbar: false})}
            autoHideDuration={3000}
            message="Inventory refreshed"
          />
          <InventoryCsvUploadDialog
            open={showUploadDialog}
            onClose={() => this.setState({showUploadDialog: false})}
            onSuccess={() => this.setState({showUploadSuccessSnackbar: true})}
          />
          <Snackbar
            anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
            open={showUploadSuccessSnackbar}
            onClose={() => this.setState({showUploadSuccessSnackbar: false})}
            autoHideDuration={3000}
            message="Inventory uploaded successfully"
          />
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: any) => {
  return {
    user: state.user,
    productState: state.products,
    productDepotInfo: state.productDepotInfos,
    subSubCategoriesState: state.subSubCategories,
    depotState: state.depots,
  };
};

export default connect(mapStateToProps)(Inventory);
